<template>
    <div class="flex flex-col min-h-screen overflow-x-clip">
        <div v-if="digistormSettings?.fontHtml" v-html="digistormSettings.fontHtml" />

        <Navigation page-type="page" />

        <div class="py-20 container-extra-wide mx-auto">
            <div class="flex items-center justify-center">
                <div class="flex flex-col justify-center items-center space-y-4">
                    <p class="text-center text-neutral-base text-sm leading-sm font-ux font-semibold">{{ error.statusCode }}</p>
                    <h1 class="text-center text-5xl leading-5xl font-brand-heading font-heading">{{ error.statusMessage ?? 'Something went wrong' }}</h1>

                    <div v-if="error.message" class="flex justify-center max-w-[450px] text-center ">
                        <p class="text-neutral-base text-base leading-base whitespace-pre-wrap" v-html="error.message" />
                    </div>
                    <DButton v-if="error.stack" theme="transparent" left-icon="code-bracket-16-solid" @click="showStack = !showStack">See stack</DButton>
                    <pre v-if="showStack" class="text-xs" v-html="error.stack"/>

                <!-- <div v-if="error.statusCode !== 500" class="w-full flex justify-center pt-10">
                        <NuxtLink to="/">
                            <DButton size="lg">Back to homepage</DButton>
                        </NuxtLink>
                    </div> -->
                </div>
            </div>
        </div>

        <div class="mt-auto">
            <Footer />
        </div>
    </div>
</template>

<script setup>
    import { DButton } from '@digistorm/spark'

    defineProps({
        error: Object
    })

    const showStack = ref(false)

    const globalStore = useGlobalStore()
    const { digistormSettings, globalSets, themeCss, fontWeightCss, getGlobalTheme } = storeToRefs(globalStore)
    const { roundness, btnRoundness } = useRoundness(digistormSettings)

    await globalStore.loadSite()

    const addFavicon = () => {
        const logos = find(globalSets.value, { name: 'Logos'})
        const favicon = get(logos, 'logos_BrowserIcon[0].url')
        if (!favicon) {
            return
        }
        useHead({
            htmlAttrs: {
                lang: 'en'
            },
            link: [
                {
                    rel: 'icon',
                    type: 'image/x-icon',
                    href: favicon
                }
            ]
        })
    }

    const addGlobalCode = () => {
        const globalCode = find(globalSets.value, { __typename: 'ds_GlobalCode_GlobalSet' })
        const headCode = get(globalCode, 'globalCode_HeadCode')
        const bodyCode = get(globalCode, 'globalCode_BodyCode')

        if (headCode) {
            const headElement = document.createElement('div')
            headElement.id = 'global-head-code'
            headElement.innerHTML = headCode
            document.head.appendChild(headElement)

            const scripts = []

            // Extract all scripts from the head code
            const headScripts = headElement.getElementsByTagName('script')
            for (const script of headScripts) {
                if (script.src) {
                    scripts.push({
                        src: script.src ?? '',
                        type: script.type || 'text/javascript',
                    })
                } else {
                    scripts.push({
                        innerHTML: script.textContent ?? '',
                        type: script.type || 'text/javascript',
                    })
                }

            }

            useHead({
                script: scripts
            })
        }

        if (bodyCode) {
            const bodyElement = document.createElement('div')
            bodyElement.id = 'global-body-code'
            bodyElement.innerHTML = bodyCode
            document.body.appendChild(bodyElement)

            // Extract and execute any script tags in the body code
            const bodyScripts = bodyElement.getElementsByTagName('script')
            for (const script of bodyScripts) {
                const newScript = document.createElement('script')
                newScript.type = script.type || 'text/javascript'
                if (script.src) {
                    newScript.src = script.src
                } else {
                    newScript.textContent = script.textContent
                }
                document.body.appendChild(newScript)
            }
        }

    }

    onMounted(() => {
        addFavicon()
        addGlobalCode()
    })

    each(themeCss.value, (styles) => {
        useHead({
            style: [
                {
                    innerHTML: styles.css,
                    id: `site-theme-${styles.id}`,
                },
            ]
        })
    })

    useHead({
        htmlAttrs: {
            'data-theme': getGlobalTheme.value,
            'data-mode': 'base',
            'data-radius': roundness.value,
            'data-button-radius': btnRoundness.value,
            class: 'surface-primary font-ux'
        },
        style: [
            {
                innerHTML: fontWeightCss.value,
                id: 'site-font-weights',
            },
        ]
    })

</script>
